import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { WizardStepIndicator } from './WizardStepIndicator';
import type {
  BusinessModelType,
  FinancialModelInputs,
  SaasInputs,
  OneTimeSalesInputs,
  ConsultingInputs,
} from '@/types/financial-model';

// ---- Props ----

interface FinancialModelWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (inputs: FinancialModelInputs, projectId: string) => void;
  projects: Array<{ id: string; name: string }>;
  isLoading?: boolean;
  initialData?: FinancialModelInputs & { projectId?: string };
}

// ---- Month labels ----

const MONTH_KEYS = [
  'wizard.months.jan', 'wizard.months.feb', 'wizard.months.mar', 'wizard.months.apr',
  'wizard.months.may', 'wizard.months.jun', 'wizard.months.jul', 'wizard.months.aug',
  'wizard.months.sep', 'wizard.months.oct', 'wizard.months.nov', 'wizard.months.dec',
];

// ---- Business model card data ----

const MODEL_CARDS: Array<{
  type: BusinessModelType;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
}> = [
  {
    type: 'saas',
    titleKey: 'wizard.modelSaas.title',
    descriptionKey: 'wizard.modelSaas.description',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    type: 'one_time',
    titleKey: 'wizard.modelOneTime.title',
    descriptionKey: 'wizard.modelOneTime.description',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
        <path d="M6 21h12" />
        <path d="M12 17v4" />
      </svg>
    ),
  },
  {
    type: 'consulting',
    titleKey: 'wizard.modelConsulting.title',
    descriptionKey: 'wizard.modelConsulting.description',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
];

// ---- Default form data ----

function getDefaultFormData(): FormState {
  return {
    modelType: null,
    projectId: '',
    modelName: '',
    initialInvestment: 0,

    // SaaS
    subscriptionPrice: 0,
    conversionLeadToFreemium: 10,
    conversionFreemiumToPaid: 5,
    monthlyChurnRate: 5,
    apiCostPerUser: 0,

    // One-time
    productPrice: 0,
    cogs: 0,
    conversionLeadToSale: 3,
    repeatPurchaseRate: 10,

    // Consulting
    avgProjectValue: 0,
    utilizationRate: 70,
    maxClientsPerMonth: 5,
    conversionLeadToClient: 5,

    // Revenue & Funnel
    expectedMonthlyReachUniform: 1000,
    expectedMonthlyReach: new Array(12).fill(1000) as number[],
    isReachUniform: true,
    conversionWebsiteToLead: 3,

    // Costs
    monthlyAdBudgetUniform: 0,
    monthlyAdBudgets: new Array(12).fill(0) as number[],
    isAdBudgetUniform: true,
    monthlyInfrastructureCost: 0,
    taxRate: 6,
    paymentProcessingFee: 3.5,
  };
}

// ---- Internal form state (wider than the output type) ----

interface FormState {
  modelType: BusinessModelType | null;
  projectId: string;
  modelName: string;
  initialInvestment: number;

  // SaaS
  subscriptionPrice: number;
  conversionLeadToFreemium: number;
  conversionFreemiumToPaid: number;
  monthlyChurnRate: number;
  apiCostPerUser: number;

  // One-time
  productPrice: number;
  cogs: number;
  conversionLeadToSale: number;
  repeatPurchaseRate: number;

  // Consulting
  avgProjectValue: number;
  utilizationRate: number;
  maxClientsPerMonth: number;
  conversionLeadToClient: number;

  // Revenue & Funnel
  expectedMonthlyReachUniform: number;
  expectedMonthlyReach: number[];
  isReachUniform: boolean;
  conversionWebsiteToLead: number;

  // Costs
  monthlyAdBudgetUniform: number;
  monthlyAdBudgets: number[];
  isAdBudgetUniform: boolean;
  monthlyInfrastructureCost: number;
  taxRate: number;
  paymentProcessingFee: number;
}

// ---- Helpers ----

/** Sanitise a numeric input value, returning 0 for empty / NaN */
function toNum(value: string): number {
  if (value === '' || value === '-') return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

/** Format number with display-friendly separators */
function displayNum(n: number): string {
  if (n === 0) return '';
  return String(n);
}

// ---- Convert initialData to FormState ----

function inputsToFormState(data: FinancialModelInputs & { projectId?: string }): FormState {
  const base = getDefaultFormData();

  const reachArr = data.expectedMonthlyReach ?? new Array(12).fill(1000);
  const adArr = data.monthlyAdBudgets ?? new Array(12).fill(0);
  const isReachUniform = new Set(reachArr).size <= 1;
  const isAdUniform = new Set(adArr).size <= 1;

  const common: Partial<FormState> = {
    modelType: data.modelType,
    projectId: data.projectId ?? '',
    modelName: data.modelName,
    initialInvestment: data.initialInvestment,
    conversionWebsiteToLead: data.conversionWebsiteToLead,
    expectedMonthlyReach: reachArr,
    expectedMonthlyReachUniform: reachArr[0] ?? 1000,
    isReachUniform,
    monthlyAdBudgets: adArr,
    monthlyAdBudgetUniform: adArr[0] ?? 0,
    isAdBudgetUniform: isAdUniform,
    monthlyInfrastructureCost: data.monthlyInfrastructureCost,
    taxRate: data.taxRate,
    paymentProcessingFee: data.paymentProcessingFee,
  };

  if (data.modelType === 'saas') {
    return { ...base, ...common, subscriptionPrice: data.subscriptionPrice, conversionLeadToFreemium: data.conversionLeadToFreemium, conversionFreemiumToPaid: data.conversionFreemiumToPaid, monthlyChurnRate: data.monthlyChurnRate, apiCostPerUser: data.apiCostPerUser };
  }
  if (data.modelType === 'one_time') {
    return { ...base, ...common, productPrice: data.productPrice, cogs: data.cogs, conversionLeadToSale: data.conversionLeadToSale, repeatPurchaseRate: data.repeatPurchaseRate };
  }
  if (data.modelType === 'consulting') {
    return { ...base, ...common, avgProjectValue: data.avgProjectValue, utilizationRate: data.utilizationRate, maxClientsPerMonth: data.maxClientsPerMonth, conversionLeadToClient: data.conversionLeadToClient };
  }
  return { ...base, ...common };
}

// ---- Component ----

export function FinancialModelWizard({
  isOpen,
  onClose,
  onSubmit,
  projects,
  isLoading = false,
  initialData,
}: FinancialModelWizardProps) {
  const { t, i18n } = useTranslation('financial-model');
  const currencySymbol = i18n.language === 'en' ? '$' : '₽';
  const perMonthSuffix = i18n.language === 'en' ? '/mo' : '/мес';
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState<FormState>(getDefaultFormData);

  // Reset on open — pre-fill if editing
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setForm(initialData ? inputsToFormState(initialData) : getDefaultFormData());
    }
  }, [isOpen, initialData]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // ---- Field updaters ----

  const updateField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateMonthlyArray = useCallback(
    (field: 'expectedMonthlyReach' | 'monthlyAdBudgets', index: number, value: number) => {
      setForm(prev => {
        const arr = [...prev[field]];
        arr[index] = value;
        return { ...prev, [field]: arr };
      });
    },
    []
  );

  // Keep uniform values synced
  useEffect(() => {
    if (form.isReachUniform) {
      setForm(prev => ({
        ...prev,
        expectedMonthlyReach: new Array(12).fill(prev.expectedMonthlyReachUniform) as number[],
      }));
    }
  }, [form.isReachUniform, form.expectedMonthlyReachUniform]);

  useEffect(() => {
    if (form.isAdBudgetUniform) {
      setForm(prev => ({
        ...prev,
        monthlyAdBudgets: new Array(12).fill(prev.monthlyAdBudgetUniform) as number[],
      }));
    }
  }, [form.isAdBudgetUniform, form.monthlyAdBudgetUniform]);

  // ---- Step validation ----

  const isStep0Valid = form.modelType !== null && form.modelName.trim() !== '';

  const isStep1Valid = (() => {
    if (form.conversionWebsiteToLead <= 0) return false;
    const hasReach = form.isReachUniform
      ? form.expectedMonthlyReachUniform > 0
      : form.expectedMonthlyReach.some(v => v > 0);
    if (!hasReach) return false;

    if (form.modelType === 'saas') {
      return form.conversionLeadToFreemium > 0 && form.conversionFreemiumToPaid > 0;
    }
    if (form.modelType === 'one_time') {
      return form.conversionLeadToSale > 0;
    }
    if (form.modelType === 'consulting') {
      return form.conversionLeadToClient > 0 && form.maxClientsPerMonth > 0;
    }
    return false;
  })();

  const isStep2Valid = form.taxRate >= 0 && form.paymentProcessingFee >= 0;

  const canGoNext = [isStep0Valid, isStep1Valid, isStep2Valid, true][currentStep];

  // ---- Navigation ----

  const goNext = () => {
    if (currentStep < 3 && canGoNext) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // ---- Submit ----

  const handleSubmit = () => {
    const common = {
      modelName: form.modelName,
      initialInvestment: form.initialInvestment,
      monthlyAdBudgets: form.monthlyAdBudgets,
      taxRate: form.taxRate,
      paymentProcessingFee: form.paymentProcessingFee,
      monthlyInfrastructureCost: form.monthlyInfrastructureCost,
      expectedMonthlyReach: form.expectedMonthlyReach,
      conversionWebsiteToLead: form.conversionWebsiteToLead,
    };

    let result: FinancialModelInputs;

    if (form.modelType === 'saas') {
      result = {
        ...common,
        modelType: 'saas',
        subscriptionPrice: form.subscriptionPrice,
        conversionLeadToFreemium: form.conversionLeadToFreemium,
        conversionFreemiumToPaid: form.conversionFreemiumToPaid,
        monthlyChurnRate: form.monthlyChurnRate,
        apiCostPerUser: form.apiCostPerUser,
      } satisfies SaasInputs;
    } else if (form.modelType === 'one_time') {
      result = {
        ...common,
        modelType: 'one_time',
        productPrice: form.productPrice,
        cogs: form.cogs,
        conversionLeadToSale: form.conversionLeadToSale,
        repeatPurchaseRate: form.repeatPurchaseRate,
      } satisfies OneTimeSalesInputs;
    } else {
      result = {
        ...common,
        modelType: 'consulting',
        avgProjectValue: form.avgProjectValue,
        utilizationRate: form.utilizationRate,
        maxClientsPerMonth: form.maxClientsPerMonth,
        conversionLeadToClient: form.conversionLeadToClient,
      } satisfies ConsultingInputs;
    }

    onSubmit(result, form.projectId);
  };

  // ---- Reusable field renderers ----

  const renderNumberInput = (
    label: string,
    value: number,
    onChange: (v: number) => void,
    options?: { suffix?: string; placeholder?: string; min?: number; step?: string; hint?: string }
  ) => (
    <div className="flex flex-col gap-[4px]">
      <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
        {label}
      </label>
      {options?.hint && (
        <span className="font-segoe text-[12px] leading-[16px] text-[#969696]">
          {options.hint}
        </span>
      )}
      <div className="relative">
        <input
          type="number"
          value={displayNum(value)}
          onChange={e => onChange(toNum(e.target.value))}
          min={options?.min ?? 0}
          step={options?.step ?? 'any'}
          placeholder={options?.placeholder ?? '0'}
          className={cn(
            'w-full bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]',
            "font-segoe text-[16px] leading-[24px] text-[#0d0d0d]",
            'placeholder:text-[#969696] outline-none',
            options?.suffix && 'pr-[48px]'
          )}
        />
        {options?.suffix && (
          <span className="absolute right-[16px] top-1/2 -translate-y-1/2 font-segoe text-[14px] text-[#969696] pointer-events-none">
            {options.suffix}
          </span>
        )}
      </div>
    </div>
  );

  const renderTextInput = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    options?: { placeholder?: string; required?: boolean }
  ) => (
    <div className="flex flex-col gap-[4px]">
      <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
        {label}{options?.required ? '*' : ''}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={options?.placeholder ?? ''}
        className={cn(
          'w-full bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]',
          "font-segoe text-[16px] leading-[24px] text-[#0d0d0d]",
          'placeholder:text-[#969696] outline-none'
        )}
      />
    </div>
  );

  /** 12-month grid with uniform toggle */
  const renderMonthlyInput = (
    label: string,
    isUniform: boolean,
    uniformValue: number,
    monthlyValues: number[],
    onToggle: (v: boolean) => void,
    onUniformChange: (v: number) => void,
    onMonthChange: (index: number, v: number) => void,
    suffix?: string,
    hint?: string
  ) => (
    <div className="flex flex-col gap-[8px]">
      <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
        {label}
      </label>
      {hint && (
        <span className="font-segoe text-[12px] leading-[16px] text-[#969696]">
          {hint}
        </span>
      )}

      {/* Uniform single input */}
      <div className="relative">
        <input
          type="number"
          value={displayNum(isUniform ? uniformValue : (monthlyValues[0] ?? 0))}
          onChange={e => {
            if (isUniform) {
              onUniformChange(toNum(e.target.value));
            } else {
              onMonthChange(0, toNum(e.target.value));
            }
          }}
          min={0}
          step="any"
          placeholder="0"
          className={cn(
            'w-full bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]',
            "font-segoe text-[16px] leading-[24px] text-[#0d0d0d]",
            'placeholder:text-[#969696] outline-none',
            suffix && 'pr-[48px]'
          )}
        />
        {suffix && (
          <span className="absolute right-[16px] top-1/2 -translate-y-1/2 font-segoe text-[14px] text-[#969696] pointer-events-none">
            {suffix}
          </span>
        )}
      </div>

      {/* Uniform checkbox */}
      <label className="flex items-center gap-[8px] cursor-pointer select-none">
        <input
          type="checkbox"
          checked={isUniform}
          onChange={e => onToggle(e.target.checked)}
          className="w-[18px] h-[18px] accent-[#2197f1] cursor-pointer"
        />
        <span className="font-segoe text-[14px] leading-[20px] text-[#565858]">
          {t('wizard.uniformCheckbox')}
        </span>
      </label>

      {/* Per-month grid */}
      {!isUniform && (
        <div className="grid grid-cols-4 gap-[8px]">
          {monthlyValues.map((val, i) => (
            <div key={i} className="flex flex-col gap-[2px]">
              <span className="font-segoe text-[11px] leading-[14px] text-[#969696] text-center">
                {t(MONTH_KEYS[i]!)}
              </span>
              <input
                type="number"
                value={displayNum(val)}
                onChange={e => onMonthChange(i, toNum(e.target.value))}
                min={0}
                step="any"
                placeholder="0"
                className={cn(
                  'w-full bg-transparent border border-[#eeeeee] rounded-[8px] px-[8px] py-[6px]',
                  "font-segoe text-[13px] leading-[18px] text-[#0d0d0d] text-center",
                  'placeholder:text-[#969696] outline-none'
                )}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ---- Step content renderers ----

  const renderStep0 = () => (
    <div className="flex flex-col gap-[24px]">
      {/* Business model type cards */}
      <div className="flex flex-col gap-[8px]">
        <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
          {t('wizard.labelModelType')}
        </label>
        <div className="flex flex-col gap-[12px]">
          {MODEL_CARDS.map(card => {
            const isSelected = form.modelType === card.type;
            return (
              <button
                key={card.type}
                type="button"
                onClick={() => updateField('modelType', card.type)}
                className={cn(
                  'flex items-start gap-[12px] p-[16px] rounded-[16px] border-[2px] text-left transition-colors',
                  isSelected
                    ? 'border-[#2197f1] bg-[rgba(33,151,241,0.05)]'
                    : 'border-[#eeeeee] bg-white hover:border-[#ccc]'
                )}
              >
                <div
                  className={cn(
                    'w-[40px] h-[40px] rounded-[8px] flex items-center justify-center shrink-0',
                    isSelected ? 'bg-[#2197f1] text-white' : 'bg-[#f8f8f8] text-[#565858]'
                  )}
                >
                  {card.icon}
                </div>
                <div className="flex flex-col gap-[2px]">
                  <span
                    className={cn(
                      "font-manrope font-bold text-[16px] leading-[22px]",
                      isSelected ? 'text-[#2197f1]' : 'text-[#0d0d0d]'
                    )}
                  >
                    {t(card.titleKey)}
                  </span>
                  <span className="font-segoe text-[13px] leading-[18px] text-[#969696]">
                    {t(card.descriptionKey)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Project selection */}
      <div className="flex flex-col gap-[4px]">
        <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
          {t('wizard.labelProduct')}
        </label>
        <Select
          value={form.projectId}
          onValueChange={v => updateField('projectId', v)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('wizard.placeholderProduct')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Model name */}
      {renderTextInput(
        t('wizard.labelModelName'),
        form.modelName,
        v => updateField('modelName', v),
        { placeholder: t('wizard.placeholderModelName'), required: true }
      )}

      {/* Initial investment */}
      {renderNumberInput(
        t('wizard.labelInitialInvestment'),
        form.initialInvestment,
        v => updateField('initialInvestment', v),
        { suffix: currencySymbol }
      )}

      {/* Conditional price fields */}
      {form.modelType === 'saas' && (
        renderNumberInput(
          t('wizard.labelSubscriptionPrice'),
          form.subscriptionPrice,
          v => updateField('subscriptionPrice', v),
          { suffix: currencySymbol + perMonthSuffix }
        )
      )}

      {form.modelType === 'one_time' && (
        <>
          {renderNumberInput(
            t('wizard.labelProductPrice'),
            form.productPrice,
            v => updateField('productPrice', v),
            { suffix: currencySymbol }
          )}
          {renderNumberInput(
            t('wizard.labelCogs'),
            form.cogs,
            v => updateField('cogs', v),
            { suffix: currencySymbol }
          )}
        </>
      )}

      {form.modelType === 'consulting' && (
        renderNumberInput(
          t('wizard.labelAvgProjectValue'),
          form.avgProjectValue,
          v => updateField('avgProjectValue', v),
          { suffix: currencySymbol }
        )
      )}
    </div>
  );

  const renderStep1 = () => (
    <div className="flex flex-col gap-[24px]">
      {/* Monthly reach */}
      {renderMonthlyInput(
        t('wizard.labelMonthlyReach'),
        form.isReachUniform,
        form.expectedMonthlyReachUniform,
        form.expectedMonthlyReach,
        v => updateField('isReachUniform', v),
        v => updateField('expectedMonthlyReachUniform', v),
        (i, v) => updateMonthlyArray('expectedMonthlyReach', i, v),
        undefined,
        t('wizard.hintMonthlyReach')
      )}

      {/* Conversion: website to lead */}
      {renderNumberInput(
        t('wizard.labelConversionSiteToLead'),
        form.conversionWebsiteToLead,
        v => updateField('conversionWebsiteToLead', v),
        { suffix: '%', hint: t('wizard.hintConversionSiteToLead') }
      )}

      {/* Conditional conversion fields */}
      {form.modelType === 'saas' && (
        <>
          {renderNumberInput(
            t('wizard.labelConversionLeadToFreemium'),
            form.conversionLeadToFreemium,
            v => updateField('conversionLeadToFreemium', v),
            { suffix: '%', hint: t('wizard.hintConversionLeadToFreemium') }
          )}
          {renderNumberInput(
            t('wizard.labelConversionFreemiumToPaid'),
            form.conversionFreemiumToPaid,
            v => updateField('conversionFreemiumToPaid', v),
            { suffix: '%', hint: t('wizard.hintConversionFreemiumToPaid') }
          )}
          {renderNumberInput(
            t('wizard.labelMonthlyChurn'),
            form.monthlyChurnRate,
            v => updateField('monthlyChurnRate', v),
            { suffix: '%', hint: t('wizard.hintMonthlyChurn') }
          )}
        </>
      )}

      {form.modelType === 'one_time' && (
        <>
          {renderNumberInput(
            t('wizard.labelConversionLeadToSale'),
            form.conversionLeadToSale,
            v => updateField('conversionLeadToSale', v),
            { suffix: '%', hint: t('wizard.hintConversionLeadToSale') }
          )}
          {renderNumberInput(
            t('wizard.labelRepeatPurchaseRate'),
            form.repeatPurchaseRate,
            v => updateField('repeatPurchaseRate', v),
            { suffix: '%', hint: t('wizard.hintRepeatPurchaseRate') }
          )}
        </>
      )}

      {form.modelType === 'consulting' && (
        <>
          {renderNumberInput(
            t('wizard.labelConversionLeadToClient'),
            form.conversionLeadToClient,
            v => updateField('conversionLeadToClient', v),
            { suffix: '%', hint: t('wizard.hintConversionLeadToClient') }
          )}
          {renderNumberInput(
            t('wizard.labelUtilizationRate'),
            form.utilizationRate,
            v => updateField('utilizationRate', v),
            { suffix: '%', hint: t('wizard.hintUtilizationRate') }
          )}
          {renderNumberInput(
            t('wizard.labelMaxClients'),
            form.maxClientsPerMonth,
            v => updateField('maxClientsPerMonth', v),
            { hint: t('wizard.hintMaxClients') }
          )}
        </>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="flex flex-col gap-[24px]">
      {/* Monthly ad budget */}
      {renderMonthlyInput(
        t('wizard.labelAdBudget'),
        form.isAdBudgetUniform,
        form.monthlyAdBudgetUniform,
        form.monthlyAdBudgets,
        v => updateField('isAdBudgetUniform', v),
        v => updateField('monthlyAdBudgetUniform', v),
        (i, v) => updateMonthlyArray('monthlyAdBudgets', i, v),
        currencySymbol
      )}

      {/* API cost per user (SaaS only) */}
      {form.modelType === 'saas' && (
        renderNumberInput(
          t('wizard.labelApiCostPerUser'),
          form.apiCostPerUser,
          v => updateField('apiCostPerUser', v),
          { suffix: currencySymbol + perMonthSuffix }
        )
      )}

      {/* Infrastructure */}
      {renderNumberInput(
        t('wizard.labelInfrastructure'),
        form.monthlyInfrastructureCost,
        v => updateField('monthlyInfrastructureCost', v),
        { suffix: currencySymbol + perMonthSuffix }
      )}

      {/* Tax rate */}
      {renderNumberInput(
        t('wizard.labelTaxRate'),
        form.taxRate,
        v => updateField('taxRate', v),
        { suffix: '%' }
      )}

      {/* Payment processing fee */}
      {renderNumberInput(
        t('wizard.labelPaymentFee'),
        form.paymentProcessingFee,
        v => updateField('paymentProcessingFee', v),
        { suffix: '%' }
      )}
    </div>
  );

  const renderStep3 = () => {
    const modelTypeLabel =
      form.modelType === 'saas' ? t('wizard.modelSaas.title')
        : form.modelType === 'one_time' ? t('wizard.modelOneTime.title')
          : t('wizard.modelConsulting.title');

    const projectName = projects.find(p => p.id === form.projectId)?.name;

    const formatMoney = (v: number) =>
      v.toLocaleString('ru-RU') + ' \u20BD';

    const formatPercent = (v: number) => `${v}%`;

    const formatArray = (arr: number[], isUniform: boolean, formatter: (v: number) => string) => {
      if (isUniform || new Set(arr).size === 1) {
        return formatter(arr[0] ?? 0);
      }
      return arr.map((v, i) => `${t(MONTH_KEYS[i]!)}: ${formatter(v)}`).join(', ');
    };

    const sections: Array<{
      title: string;
      items: Array<{ label: string; value: string }>;
    }> = [
      {
        title: t('wizard.summary.mainParams'),
        items: [
          { label: t('wizard.summary.modelType'), value: modelTypeLabel },
          ...(projectName ? [{ label: t('wizard.summary.product'), value: projectName }] : []),
          { label: t('wizard.summary.modelName'), value: form.modelName },
          { label: t('wizard.summary.initialInvestment'), value: formatMoney(form.initialInvestment) },
        ],
      },
    ];

    // Price section
    const baseSection = sections[0]!;
    if (form.modelType === 'saas') {
      baseSection.items.push({ label: t('wizard.summary.subscriptionPrice'), value: formatMoney(form.subscriptionPrice) + perMonthSuffix });
    } else if (form.modelType === 'one_time') {
      baseSection.items.push(
        { label: t('wizard.summary.productPrice'), value: formatMoney(form.productPrice) },
        { label: t('wizard.summary.costOfGoods'), value: formatMoney(form.cogs) }
      );
    } else {
      baseSection.items.push({ label: t('wizard.summary.avgProjectValue'), value: formatMoney(form.avgProjectValue) });
    }

    // Revenue & Funnel
    const funnelItems: Array<{ label: string; value: string }> = [
      {
        label: t('wizard.summary.reachPerMonth'),
        value: formatArray(form.expectedMonthlyReach, form.isReachUniform, v => v.toLocaleString('ru-RU')),
      },
      { label: t('wizard.summary.siteToLead'), value: formatPercent(form.conversionWebsiteToLead) },
    ];

    if (form.modelType === 'saas') {
      funnelItems.push(
        { label: t('wizard.summary.leadToFreemium'), value: formatPercent(form.conversionLeadToFreemium) },
        { label: t('wizard.summary.freemiumToPaid'), value: formatPercent(form.conversionFreemiumToPaid) },
        { label: t('wizard.summary.churnPerMonth'), value: formatPercent(form.monthlyChurnRate) }
      );
    } else if (form.modelType === 'one_time') {
      funnelItems.push(
        { label: t('wizard.summary.leadToSale'), value: formatPercent(form.conversionLeadToSale) },
        { label: t('wizard.summary.repeatPurchases'), value: formatPercent(form.repeatPurchaseRate) }
      );
    } else {
      funnelItems.push(
        { label: t('wizard.summary.leadToClient'), value: formatPercent(form.conversionLeadToClient) },
        { label: t('wizard.summary.utilization'), value: formatPercent(form.utilizationRate) },
        { label: t('wizard.summary.maxClientsPerMonth'), value: String(form.maxClientsPerMonth) }
      );
    }

    sections.push({ title: t('wizard.summary.revenueAndFunnel'), items: funnelItems });

    // Costs
    const costItems: Array<{ label: string; value: string }> = [
      {
        label: t('wizard.summary.adPerMonth'),
        value: formatArray(form.monthlyAdBudgets, form.isAdBudgetUniform, formatMoney),
      },
    ];

    if (form.modelType === 'saas') {
      costItems.push({ label: t('wizard.summary.apiPerUser'), value: formatMoney(form.apiCostPerUser) + perMonthSuffix });
    }

    costItems.push(
      { label: t('wizard.summary.infraPerMonth'), value: formatMoney(form.monthlyInfrastructureCost) },
      { label: t('wizard.summary.tax'), value: formatPercent(form.taxRate) },
      { label: t('wizard.summary.paymentFee'), value: formatPercent(form.paymentProcessingFee) }
    );

    sections.push({ title: t('wizard.summary.expenses'), items: costItems });

    return (
      <div className="flex flex-col gap-[16px]">
        {sections.map((section, si) => (
          <div
            key={si}
            className="bg-[#f8f8f8] rounded-[16px] p-[16px] flex flex-col gap-[8px]"
          >
            <h4 className="font-manrope font-bold text-[14px] leading-[20px] text-[#0d0d0d]">
              {section.title}
            </h4>
            {section.items.map((item, ii) => (
              <div key={ii} className="flex justify-between gap-[8px]">
                <span className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {item.label}
                </span>
                <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d] text-right font-semibold">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // ---- Render ----

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-[16px]"
      onClick={e => {
        if (e.target === e.currentTarget && !isLoading) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-[24px] max-w-[640px] w-full max-h-[90vh] flex flex-col overflow-hidden">
        {isLoading ? (
          <div className="p-[24px] md:p-[40px]">
            <GenerationProgress
              title={t('wizard.title')}
              message={t('wizard.progressMessage')}
            />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex flex-col gap-[16px] px-[24px] md:px-[40px] pt-[24px] md:pt-[40px] pb-[16px] flex-shrink-0">
              <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d]">
                {t('wizard.title')}
              </h2>
              <WizardStepIndicator currentStep={currentStep} totalSteps={4} />
            </div>

            {/* Step content */}
            <div className="px-[24px] md:px-[40px] pb-[24px] overflow-y-auto flex-1 min-h-0">
              {currentStep === 0 && renderStep0()}
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
            </div>

            {/* Action buttons */}
            <div className="flex gap-[16px] px-[24px] md:px-[40px] pb-[24px] md:pb-[40px] pt-[8px] flex-shrink-0">
              {currentStep > 0 ? (
                <button
                  type="button"
                  onClick={goBack}
                  className="flex-1 bg-transparent border border-[var(--grey-accent,#eeeeee)] border-solid text-[color:var(--black-text,#0d0d0d)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] text-center hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors"
                >
                  {t('wizard.buttonBack')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-transparent border border-[var(--grey-accent,#eeeeee)] border-solid text-[color:var(--black-text,#0d0d0d)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] text-center hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors"
                >
                  {t('wizard.buttonClose')}
                </button>
              )}

              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!canGoNext}
                  className={cn(
                    'flex-1 rounded-[16px] px-[24px] py-[12px]',
                    "font-segoe font-semibold text-[16px] leading-[24px] text-center transition-colors",
                    canGoNext
                      ? 'bg-[#2197f1] text-white hover:bg-[#1a7bc7]'
                      : 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] cursor-not-allowed opacity-60'
                  )}
                >
                  {t('wizard.buttonNext')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="flex-1 bg-[#2197f1] text-white rounded-[16px] px-[24px] py-[12px] font-segoe font-semibold text-[16px] leading-[24px] text-center hover:bg-[#1a7bc7] transition-colors"
                >
                  {t('wizard.buttonCreate')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
